import { Typography, Grid, CircularProgress } from "@material-ui/core";
import CustomDatePickerInput from "components/CustomDatePickerInput";
import SanitizedGrid from "components/SanitizedGrid";
import moment from "moment";
import {
  AutocompleteInput,
  Button,
  CreateProps,
  FormDataConsumer,
  maxLength,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  useMutation,
  useNotify,
  useRedirect,
} from "react-admin";
import { GetParam } from "utils/tools";
import { typeInvoice } from "./conts";
import SaveIcon from "@material-ui/icons/Save";
import { useFormState } from "react-final-form";
import PriceInput from "./components/PriceInput";
import { useEffect, useState } from "react";
import TbfCodeInput from "pages/ratecharts/components/TbfCodeInput";
import ProjectCodeInput from "./components/ProjectCodeInput";
import InvoiceInCreditNoteInput from "./components/InvoiceInCreditNoteInput";
import DeferralCodeInput from "./components/DeferralCodeInput";
import { disabledCommercialDocumentCreateButton } from "utils/validation";
import CreateWrapper from "../../components/CreateWrapper";

const CommercialDocumentCreate = (props: CreateProps) => {
  const param = GetParam();
  const personType = param.get("personType");
  const title =
    personType === "personInCharges" ? "Person In Charge" : "Parents";

  const breadcrumb = [
    {
      title: title,
      link: `/${personType}/${param.get("parentId")}/show`,
    },
    {
      title: "Financial",
      link: `/${personType}/${param.get("parentId")}/show/1`,
    },
    {
      title: `Create ${
        param.get("type") === "Invoice"
          ? "New Adhoc Invoice"
          : "New Credit Note"
      }`,
      link: "",
    },
  ];

  const CommercialDocumentToolbar = (props: any) => {
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const { values } = useFormState();

    const redirect = useRedirect();
    const notify = useNotify();

    useEffect(() => {
      setIsDisabled(disabledCommercialDocumentCreateButton(values));
    }, [values]);

    const [handleSaveCommercialDocument, { loading }] = useMutation(
      {
        type: "create",
        resource: "commercialDocuments",
        payload: {
          data: {
            ...values,
            status: "draft",
            commercialDocumentItem: {
              amount: Math.round(Number(values.price) * 100.0),
              schoolId: values.schoolId,
              description: values.description,
              tbfCode: values.tbfCode,
              projectCode: values.projectCode,
              deferralCode: values.deferralCode,
              deferralStartDate: values.deferralStartDate,
            },
          },
        },
      },
      {
        onSuccess: () => {
          notify(`Successfully save.`, "info");
          redirect(`/${personType}/${props.record.parentId}/show/1`);
        },
        onFailure: (error) =>
          notify(`Failed save, error: ${error.message}`, "warning"),
      },
    );

    return (
      <Toolbar {...props}>
        <Button
          onClick={handleSaveCommercialDocument}
          disabled={isDisabled || props.invalid}
          label={loading ? "SAVING..." : "SAVE"}
        >
          {loading ? (
            <CircularProgress size={18} thickness={2} />
          ) : (
            <SaveIcon />
          )}
        </Button>
      </Toolbar>
    );
  };

  return (
    <>
      <CreateWrapper
        breadcrumb={breadcrumb}
        headerTitle={
          param.get("type") === "Invoice" ? "New Invoice" : "New Credit Note"
        }
        {...props}
      >
        <SimpleForm toolbar={<CommercialDocumentToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">General Info</Typography>
            </Grid>

            <Grid item xs={8}>
              <SelectInput source="type" choices={typeInvoice} disabled />
            </Grid>

            <Grid item xs={8}>
              <ReferenceInput
                label={title}
                source="parentId"
                reference={personType}
                disabled
              >
                <AutocompleteInput
                  optionText={(choice: any) =>
                    `${choice.firstName} ${choice.lastName}`
                  }
                />
              </ReferenceInput>
            </Grid>

            {/* @TODO : make this optional first because user want to do reimbursement with CN
             ** will change this later after have a long term solution
             */}
            <FormDataConsumer>
              {({ formData }) =>
                formData.type === "credit_note" && (
                  <Grid item xs={8}>
                    <InvoiceInCreditNoteInput
                      personType={personType}
                      label="Select Invoice (Preferably, but optional)"
                      source="invoiceReferenceNumber"
                    />
                  </Grid>
                )
              }
            </FormDataConsumer>

            <FormDataConsumer>
              {({ formData }) =>
                formData.type === "invoice" && (
                  <Grid item xs={8}>
                    <CustomDatePickerInput
                      name="dueDate"
                      label="Due Date *"
                      min={moment().add(1, "day").format("Y-MM-DD")}
                    />
                  </Grid>
                )
              }
            </FormDataConsumer>

            <Grid item xs={12}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">
                {param.get("type") === "Invoice"
                  ? "Invoice Detail"
                  : "Credit Note Detail"}
              </Typography>
            </Grid>

            <Grid item xs={8}>
              <TbfCodeInput />
            </Grid>

            <Grid item xs={8}>
              <ProjectCodeInput />
            </Grid>

            <Grid item xs={8}>
              <ReferenceInput
                label="School Code"
                source="schoolId"
                reference="schools"
                filter={{ MbcRegisteredOnly: true }}
                validate={required()}
              >
                <AutocompleteInput
                  optionText={(choice: any) =>
                    `${choice.mbcCode} - ${choice.name}`
                  }
                />
              </ReferenceInput>
            </Grid>

            <Grid item xs={8}>
              <DeferralCodeInput />
            </Grid>

            <FormDataConsumer>
              {({ formData }) =>
                formData.deferralCode && (
                  <Grid item xs={8}>
                    <CustomDatePickerInput
                      name="deferralStartDate"
                      label="Deferral Start Date *"
                      min={
                        param.get("type") === "Invoice"
                          ? moment("2025-10-01").format("Y-MM-DD")
                          : moment().startOf("month").format("Y-MM-DD")
                      }
                    />
                  </Grid>
                )
              }
            </FormDataConsumer>

            <Grid item xs={8}>
              <PriceInput />
            </Grid>

            <Grid item xs={8}>
              <TextInput
                multiline={true}
                label="Description"
                source="description"
                inputProps={{ maxLength: 90 }}
                placeholder="Description is only up to 90 characters"
                validate={[required(), maxLength(90)]}
              />
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </CreateWrapper>
    </>
  );
};

export default CommercialDocumentCreate;
